/**
 * Domain enums (Onada 1) — mirror of the 43 Postgres enums declared in
 * docs/plan/01-data-model.md. Code identifiers are English (CLAUDE.md convention).
 *
 * Each enum is declared as a readonly `const` tuple (single source of truth) plus a
 * derived TS union type via `(typeof X)[number]`. The tuples are reused by zod
 * (`z.enum`) so schema validation and the type layer never drift apart.
 *
 * Order of members matches the DB enum order exactly (relevant for sort_order /
 * default-first semantics where the model declares a default).
 */

// 1. platform_user_role
export const PLATFORM_USER_ROLES = ['super_admin', 'support'] as const;
export type PlatformUserRole = (typeof PLATFORM_USER_ROLES)[number];

// 2. platform_audit_action
export const PLATFORM_AUDIT_ACTIONS = [
  'tenant_create',
  'tenant_update',
  'tenant_suspend',
  'tenant_reactivate',
  'tenant_soft_delete',
  'tenant_access',
  'impersonation_start',
  'impersonation_end',
  'login',
  'login_failed',
] as const;
export type PlatformAuditAction = (typeof PLATFORM_AUDIT_ACTIONS)[number];

// 3. tenant_status
export const TENANT_STATUSES = ['active', 'suspended', 'deactivated'] as const;
export type TenantStatus = (typeof TENANT_STATUSES)[number];

// 4. tenant_plan
export const TENANT_PLANS = ['obrador', 'pime', 'cadena'] as const;
export type TenantPlan = (typeof TENANT_PLANS)[number];

// 5. app_language
export const APP_LANGUAGES = ['ca', 'es', 'en'] as const;
export type AppLanguage = (typeof APP_LANGUAGES)[number];

// 6. tenant_sector
export const TENANT_SECTORS = [
  'forn',
  'pastisseria',
  'xarcuteria',
  'precuinats',
  'conserves',
  'generic',
] as const;
export type TenantSector = (typeof TENANT_SECTORS)[number];

// 7. user_role
export const USER_ROLES = [
  'admin',
  'production_manager',
  'operator',
  'quality',
  'purchasing',
  'sales',
  'viewer',
] as const;
export type UserRole = (typeof USER_ROLES)[number];

// 8. session_type
export const SESSION_TYPES = ['operator_shift', 'backoffice'] as const;
export type SessionType = (typeof SESSION_TYPES)[number];

// 9. session_status
export const SESSION_STATUSES = ['active', 'expired', 'revoked'] as const;
export type SessionStatus = (typeof SESSION_STATUSES)[number];

// 10. connection_mode
export const CONNECTION_MODES = ['online', 'offline'] as const;
export type ConnectionMode = (typeof CONNECTION_MODES)[number];

// 11. device_type
export const DEVICE_TYPES = [
  'label_printer',
  'scale',
  'barcode_reader',
  'sensor',
  'tablet',
] as const;
export type DeviceType = (typeof DEVICE_TYPES)[number];

// 12. device_status
export const DEVICE_STATUSES = [
  'registered',
  'active',
  'offline',
  'error',
  'decommissioned',
] as const;
export type DeviceStatus = (typeof DEVICE_STATUSES)[number];

// 13. article_type
export const ARTICLE_TYPES = [
  'raw_material',
  'intermediate',
  'finished',
  'packaging',
] as const;
export type ArticleType = (typeof ARTICLE_TYPES)[number];

// 14. article_category
export const ARTICLE_CATEGORIES = [
  'raw_material',
  'auxiliary',
  'intermediate',
  'finished',
  'packaging',
] as const;
export type ArticleCategory = (typeof ARTICLE_CATEGORIES)[number];

// 15. allergen_code — the 14 EU allergens (fixed)
export const ALLERGEN_CODES = [
  'gluten',
  'crustaceans',
  'eggs',
  'fish',
  'peanuts',
  'soybeans',
  'milk',
  'nuts',
  'celery',
  'mustard',
  'sesame',
  'sulphites',
  'lupin',
  'molluscs',
] as const;
export type AllergenCode = (typeof ALLERGEN_CODES)[number];

// 16. allergen_presence
export const ALLERGEN_PRESENCES = ['contains', 'traces'] as const;
export type AllergenPresence = (typeof ALLERGEN_PRESENCES)[number];

// 16b. food_safety_mode — how an article's Fitxa Tècnica food-safety data is produced.
// A matèria primera (and any purchased-only article) is ALWAYS 'manual': nothing
// upstream can derive its composition. A MANUFACTURED article (semielaborat /
// producte acabat) chooses: 'derived' pulls ingredients, allergens and nutrition
// from the BOM of its active recipe version; 'manual' declares them on the article
// itself (a resold good, a supplier-declared composition, a legacy sheet).
export const FOOD_SAFETY_MODES = ['manual', 'derived'] as const;
export type FoodSafetyMode = (typeof FOOD_SAFETY_MODES)[number];

// 16c. ingredients_composition_rule — com es construeix la declaració d'ingredients
// de la FT i de l'etiqueta a partir de cada línia del BOM. 'raw_material' escriu
// només el nom de la matèria primera; 'ingredients' escriu els seus ingredients
// declarats (i el nom de la MP quan no en té cap, perquè un buit al catàleg no
// pot fer desaparèixer un ingredient de l'etiqueta); 'both' escriu el nom i, entre
// parèntesis, els ingredients — «Farina T45 (farina de blat, gluten, enzims)».
// Ajust per obrador (tenant_settings), no per fitxa: la FT i l'etiqueta han de
// declarar igual, i dos productes iguals també.
export const INGREDIENTS_COMPOSITION_RULES = ['raw_material', 'ingredients', 'both'] as const;
export type IngredientsCompositionRule = (typeof INGREDIENTS_COMPOSITION_RULES)[number];

// 17. supplier_link_role
export const SUPPLIER_LINK_ROLES = ['primary', 'alternative'] as const;
export type SupplierLinkRole = (typeof SUPPLIER_LINK_ROLES)[number];

// 18. recipe_step_control_type
export const RECIPE_STEP_CONTROL_TYPES = [
  'none',
  'weight',
  'timer',
  'temperature',
  'pcc',
] as const;
export type RecipeStepControlType = (typeof RECIPE_STEP_CONTROL_TYPES)[number];

// 20. work_order_type
export const WORK_ORDER_TYPES = ['production', 'packaging'] as const;
export type WorkOrderType = (typeof WORK_ORDER_TYPES)[number];

// 21. work_order_status
export const WORK_ORDER_STATUSES = [
  'planned',
  'in_progress',
  'completed',
  'cancelled',
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

// 22. assignment_target_type
export const ASSIGNMENT_TARGET_TYPES = ['user', 'section'] as const;
export type AssignmentTargetType = (typeof ASSIGNMENT_TARGET_TYPES)[number];

// 23. work_order_step_status
export const WORK_ORDER_STEP_STATUSES = [
  'pending',
  'in_progress',
  'completed',
  'skipped',
] as const;
export type WorkOrderStepStatus = (typeof WORK_ORDER_STEP_STATUSES)[number];

// 24. incident_type
export const INCIDENT_TYPES = [
  'raw_material',
  'equipment',
  'process',
  'quality',
  'other',
] as const;
export type IncidentType = (typeof INCIDENT_TYPES)[number];

// 25. incident_severity
export const INCIDENT_SEVERITIES = ['low', 'medium', 'high'] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

// 26. movement_type
export const MOVEMENT_TYPES = [
  'reception',
  'o01_consumption',
  'production_output',
  'adjustment',
  'write_off',
  'reversal',
  'shipment', // outbound expedition (negative quantities) — Vendes
  // Traspàs d'un lloc a un altre (OBR-44): two paired movements (−origin / +destination)
  // sharing transfer_id; the article total does not change. Written by move_stock().
  'transfer',
  // Devolució de compra confirmada (OBR-117): la mercaderia surt de l'obrador i torna al
  // proveïdor. Quantitat negativa, amb el lot. NO compta com a «rebut» de la comanda.
  'purchase_return',
] as const;
export type MovementType = (typeof MOVEMENT_TYPES)[number];

// 27. movement_reason
export const MOVEMENT_REASONS = [
  'recount',
  'loss',
  'damage',
  'expired',
  'burnt',
  'dropped',
  'correction',
  'reception',
  'production',
  'consumption',
] as const;
export type MovementReason = (typeof MOVEMENT_REASONS)[number];

// 27b. stock_to_regularize.kind (OBR-143, D4) — why an existences line shows up in «A regularitzar». A line can
// carry more than one (e.g. no lot AND no place).
export const STOCK_REGULARIZE_KINDS = ['no_lot', 'negative', 'no_expiry', 'no_place'] as const;
export type StockRegularizeKind = (typeof STOCK_REGULARIZE_KINDS)[number];

// 27c. stock_state_changes.state (OBR-14, D6) — the ONLY manual states of a lot in a place. «Caducat» and «Caduca
// aviat» are never stored: they are derived from the expiry date (domain/stockStates).
export const STOCK_MANUAL_STATES = ['available', 'blocked'] as const;
export type StockManualState = (typeof STOCK_MANUAL_STATES)[number];

// 27d. stock_lot_block_reason (OBR-14) — why a lot cannot be consumed: expired (derived from the date), blocked by
// hand, a pending purchase return, or a pending reception exception (the last two are the OBR-116/117 sources).
export const STOCK_BLOCK_REASONS = ['expired', 'manual', 'return', 'exception'] as const;
export type StockBlockReason = (typeof STOCK_BLOCK_REASONS)[number];

// 28. warehouse_zone_type — OBSOLET (20260925130000): it mixed temperature and
// function. Replaced by area_function + area_stock_mode + storage_condition; the
// column survives only as a nullable leftover nobody reads or writes.
export const WAREHOUSE_ZONE_TYPES = [
  'dry',
  'refrigerated',
  'frozen',
  'production',
  'shipping',
  'quarantine',
] as const;
export type WarehouseZoneType = (typeof WAREHOUSE_ZONE_TYPES)[number];

// 28b. area_function — Tipus funcional of a Zona or Secció (Estructura de l'obrador).
// «Magatzem» is a FUNCTION of an area, never a level of the hierarchy.
export const AREA_FUNCTIONS = [
  'storage', // Magatzem
  'production', // Producció
  'reception', // Recepció
  'shipping', // Expedició
  'quality', // Qualitat (quarantena, retinguts)
  'other', // Altres
] as const;
export type AreaFunction = (typeof AREA_FUNCTIONS)[number];

// 28c. area_stock_mode — Comportament logístic of a Zona or Secció.
export const AREA_STOCK_MODES = [
  'stock', // Estoc: existences live here
  'provisioning', // Aprovisionament: a work area stock reaches to be consumed
  'none', // Sense existències: no stock is expected here
] as const;
export type AreaStockMode = (typeof AREA_STOCK_MODES)[number];

// 28d. storage_condition — Conservació of a Zona or Secció (optional).
export const STORAGE_CONDITIONS = ['ambient', 'refrigerated', 'frozen'] as const;
export type StorageCondition = (typeof STORAGE_CONDITIONS)[number];

// 29. finished_lot_status
export const FINISHED_LOT_STATUSES = [
  'available',
  'quarantined',
  'consumed',
  'expired',
] as const;
export type FinishedLotStatus = (typeof FINISHED_LOT_STATUSES)[number];

// 30. supplier_lot_status
// `returned` (OBR-117): el lot ha tornat sencer al proveïdor (les devolucions retornades en sumen tot el
// rebut). No és ni consumit ni caducat: la mercaderia ja no és a l'obrador.
export const SUPPLIER_LOT_STATUSES = [
  'available',
  'quarantined',
  'consumed',
  'expired',
  'returned',
] as const;
export type SupplierLotStatus = (typeof SUPPLIER_LOT_STATUSES)[number];

// 31. reception_status
export const RECEPTION_STATUSES = ['draft', 'confirmed', 'cancelled'] as const;
export type ReceptionStatus = (typeof RECEPTION_STATUSES)[number];

// 32. tech_sheet_status
export const TECH_SHEET_STATUSES = ['draft', 'approved', 'archived'] as const;
export type TechSheetStatus = (typeof TECH_SHEET_STATUSES)[number];

// 33. label_type
export const LABEL_TYPES = ['internal_lot', 'sales_legal'] as const;
export type LabelType = (typeof LABEL_TYPES)[number];

// 34. notification_severity
export const NOTIFICATION_SEVERITIES = ['info', 'medium', 'critical'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

// 35. notification_channel
export const NOTIFICATION_CHANNELS = ['in_app', 'push', 'email'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

// 36. notification_status
export const NOTIFICATION_STATUSES = [
  'pending',
  'sent',
  'delivered',
  'read',
  'escalated',
] as const;
export type NotificationStatus = (typeof NOTIFICATION_STATUSES)[number];

// 37. import_entity_type
export const IMPORT_ENTITY_TYPES = [
  'articles',
  'recipes',
  'suppliers',
  'clients',
  'prices',
  'stock',
] as const;
export type ImportEntityType = (typeof IMPORT_ENTITY_TYPES)[number];

// 38. import_status
export const IMPORT_STATUSES = [
  'uploaded',
  'mapping',
  'validating',
  'previewed',
  'confirmed',
  'failed',
  'cancelled',
] as const;
export type ImportStatus = (typeof IMPORT_STATUSES)[number];

// 39. import_row_status
export const IMPORT_ROW_STATUSES = [
  'pending',
  'valid',
  'warning',
  'error',
  'imported',
  'skipped',
] as const;
export type ImportRowStatus = (typeof IMPORT_ROW_STATUSES)[number];

// 40. import_merge_strategy
export const IMPORT_MERGE_STRATEGIES = [
  'create_only',
  'update_existing',
  'skip_existing',
  'conflict',
] as const;
export type ImportMergeStrategy = (typeof IMPORT_MERGE_STRATEGIES)[number];

// 40b. import_row_action — what the importer actually DID with a source row.
// Orthogonal to IMPORT_ROW_STATUSES (which only says whether the row validated):
// a row can be `valid` and still end up `unchanged` because the entity already
// existed with the same information. This is the vocabulary the user reads.
export const IMPORT_ROW_ACTIONS = [
  'created',
  'updated',
  'unchanged',
  'skipped',
  'error',
] as const;
export type ImportRowAction = (typeof IMPORT_ROW_ACTIONS)[number];

// 41. unit_dimension
export const UNIT_DIMENSIONS = ['mass', 'volume', 'count', 'length'] as const;
export type UnitDimension = (typeof UNIT_DIMENSIONS)[number];

// 42. sync_status
export const SYNC_STATUSES = ['pending', 'synced', 'conflict'] as const;
export type SyncStatus = (typeof SYNC_STATUSES)[number];

// 44. sku_numbering_mode (tenant_settings.sku_numbering_mode — text + CHECK in
// the DB, not a Postgres enum). free = the obrador types every SKU;
// sequential = one tenant-wide {seq} mask; per_type = one mask per tipologia.
export const SKU_NUMBERING_MODES = ['free', 'sequential', 'per_type'] as const;
export type SkuNumberingMode = (typeof SKU_NUMBERING_MODES)[number];

// 45. tech_sheet_design (tenant_settings.tech_sheet_design — text + CHECK, not a
// Postgres enum). The built-in FT PDF layouts the obrador can pick from:
// classic mirrors the reference sample; modern / compact are alternative styles.
export const TECH_SHEET_DESIGNS = ['classic', 'modern', 'compact'] as const;
export type TechSheetDesign = (typeof TECH_SHEET_DESIGNS)[number];

// 46. label_field — the blocks a label template can place (label_templates.
// layout_json.blocks[].field). Not a Postgres enum: it lives inside jsonb and is
// validated by the shared LabelLayout schema. Data fields resolve at generation
// time from the finished_lot / tech_sheet / article / tenant; qr/barcode/logo/
// image/spacer/custom_text are presentation blocks.
export const LABEL_FIELDS = [
  // Product identity
  'legal_denomination', // denominació legal de venda (sales_legal, mandatory)
  'product_name', // nom comercial
  // Regulatory content (sales_legal)
  'ingredients', // llista d'ingredients en ordre ponderal (mandatory)
  'allergens', // al·lèrgens destacats (mandatory)
  'nutrition', // informació nutricional /100 g
  'net_weight', // quantitat neta (mandatory)
  'conservation', // condicions de conservació
  'country_of_origin', // país d'origen
  // Business identity
  'business_name', // empresa responsable (mandatory)
  'business_address', // adreça (mandatory)
  'sanitary_registry', // registre sanitari (RGSEAA)
  // Lot / traceability
  'lot_number', // número de lot (mandatory on sales_legal)
  'production_date',
  'expiry_date', // data de caducitat
  'best_before', // consum preferent
  'quantity', // quantitat produïda (internal)
  'sku',
  // Presentation blocks
  'custom_text', // free text (uses the block caption)
  'qr', // traceability QR (internal_lot)
  'barcode', // EAN / codi de barres
  'logo', // tenant logo
  'image', // product image
  'spacer', // blank vertical gap
] as const;
export type LabelField = (typeof LABEL_FIELDS)[number];

// 43. weight_source
// 'theoretical' = the operator declared the recipe quantity WITHOUT weighing
// (spec §3: elaborations that run without individual pesatges). It is a real
// consumption declaration, not a measurement — kept distinct from 'manual' so a
// register never claims a weighing that never happened.
export const WEIGHT_SOURCES = ['manual', 'scale', 'theoretical'] as const;
export type WeightSource = (typeof WEIGHT_SOURCES)[number];

// 47. purchase_order_status (comanda de compra lifecycle — Compres i Recepció).
// DISTINCT from reception_status (the goods-in document). Partial delivery is a
// DERIVED state (Σ rebut < Σ demanat), not a line member (decision Q3): the
// comanda reads 'receiving' while part of it is still to come, and 'delivered'
// once every line is in or the office closes it by hand.
//
// Flux (OBR-190): En curs → Pendent d'enviar → Pendent d'entrega → Entregada parcialment →
// Entregada (o Cancel·lada). «Pendent d'entrega» vol dir que la comanda ja s'ha demanat al
// proveïdor: la fa passar-hi el servidor quan s'estampa `sent_at` (correu, WhatsApp confirmat o
// «Marca com a enviada»). Només 'awaiting_delivery' i 'receiving' es poden recepcionar.
export const PURCHASE_ORDER_STATUSES = [
  'in_progress', // En curs (esborrany editable)
  'pending_dispatch', // Pendent d'enviar (confirmada, encara no enviada al proveïdor) — OBR-190
  'awaiting_delivery', // Pendent d'entrega (enviada, esperant gènere)
  'receiving', // Entregada parcialment (n'ha arribat una part, queda pendent) — OBR-98
  'delivered', // Entregada (totes les línies rebudes)
  'cancelled',
] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

// 47b. purchase_order_channel (OBR-79): com rep les comandes un proveïdor.
//   email  — Obrava l'envia per correu des del seu SMTP (noreply@obrava.app).
//   whatsapp — el proveïdor la rep per WhatsApp, al número de comandes de la seva
//            fitxa (OBR-119). L'ENVIAMENT des d'Obrava encara no existeix (OBR-93:
//            enllaç wa.me o API de Meta, pendent de decisió): mentrestant es
//            tracta com «manual» — Obrava en deixa constància, no l'envia.
//   manual — el proveïdor la rep per telèfon o com sigui; Obrava només en deixa
//            constància.
// Cap canal «portal/API»: no hi ha integració definida.
export const PURCHASE_ORDER_CHANNELS = ['email', 'whatsapp', 'manual'] as const;
export type PurchaseOrderChannel = (typeof PURCHASE_ORDER_CHANNELS)[number];

// 47bb. price_change_source (OBR-131): d'on ve un canvi del preu d'Article ↔ Proveïdor.
//   manual         — la fitxa de l'article (o qualsevol camí que no declari res).
//   import         — una importació (l'id del run queda enllaçat).
//   purchase_order — l'acció explícita «Actualitza el preu del proveïdor» d'una comanda
//                    (la comanda queda enllaçada). Una comanda o una recepció al preu pactat
//                    NO crea cap canvi: l'historial és el del preu de referència.
//   legacy         — anterior a l'historial (omplert per la migració); reservat: el servidor
//                    no deixa que cap client el declari.
export const PRICE_CHANGE_SOURCES = ['manual', 'import', 'purchase_order', 'legacy'] as const;
export type PriceChangeSource = (typeof PRICE_CHANGE_SOURCES)[number];
/** Els orígens que un client pot DECLARAR en escriure un preu. */
export const DECLARABLE_PRICE_CHANGE_SOURCES = ['manual', 'import', 'purchase_order'] as const;
export type DeclarablePriceChangeSource = (typeof DECLARABLE_PRICE_CHANGE_SOURCES)[number];

// 47c. purchase_order_dispatch_result — com va anar UN enviament.
export const PURCHASE_ORDER_DISPATCH_RESULTS = ['sent', 'failed'] as const;
export type PurchaseOrderDispatchResult = (typeof PURCHASE_ORDER_DISPATCH_RESULTS)[number];

// 48. reception_control_result (per-line reception QC — conforme / no_conforme).
// NULL in the data = control not applicable / not performed (auxiliars).
export const RECEPTION_CONTROL_RESULTS = ['conforme', 'no_conforme'] as const;
export type ReceptionControlResult = (typeof RECEPTION_CONTROL_RESULTS)[number];

// 49a. reception_exception_kind (OBR-83): per què una recepció és una EXCEPCIÓ.
//   over_quantity     — ha arribat més del demanat (qualsevol excés, de la línia).
//   unordered_article — un article que no és a la comanda.
//   operator_flagged  — l'operari ha marcat una entrada (presentació diferent, etc.),
//                       amb una nota obligatòria.
// «Arriba menys» i «falta una línia» NO hi són: són una recepció parcial normal.
export const RECEPTION_EXCEPTION_KINDS = [
  'over_quantity',
  'unordered_article',
  'operator_flagged',
] as const;
export type ReceptionExceptionKind = (typeof RECEPTION_EXCEPTION_KINDS)[number];

// 49b. reception_exception_status: una excepció neix `pending` i un responsable
// (admin o compres) la resol una sola vegada. Acceptar només en deixa constància;
// rebutjar exigeix nota i tampoc no reescriu la comanda.
export const RECEPTION_EXCEPTION_STATUSES = ['pending', 'accepted', 'rejected'] as const;
export type ReceptionExceptionStatus = (typeof RECEPTION_EXCEPTION_STATUSES)[number];

// 49c. reception_exception_rejection (OBR-116): què se'n fa del gènere en REBUTJAR una
// excepció de recepció.
//   immediate_return — el transportista se l'emporta ara: la devolució neix ja Retornada
//                      i el gènere no es queda a l'estoc.
//   pending_return   — es queda a l'obrador, bloquejat (no disponible per a producció),
//                      fins que es confirmi el retorn físic.
export const RECEPTION_EXCEPTION_REJECTIONS = ['immediate_return', 'pending_return'] as const;
export type ReceptionExceptionRejection = (typeof RECEPTION_EXCEPTION_REJECTIONS)[number];

// 49d. purchase_return_status (OBR-117): Pendent = el gènere és encara a l'obrador i NO és
// disponible; Retornada = ha sortit (hi ha el moviment `purchase_return`).
export const PURCHASE_RETURN_STATUSES = ['pending', 'returned'] as const;
export type PurchaseReturnStatus = (typeof PURCHASE_RETURN_STATUSES)[number];

// 49e. purchase_return_origin (OBR-117): d'on neix una devolució. Mai no es genera sola
// pel simple fet de tancar una incidència.
export const PURCHASE_RETURN_ORIGINS = ['exception', 'incident', 'reception'] as const;
export type PurchaseReturnOrigin = (typeof PURCHASE_RETURN_ORIGINS)[number];

// 49f. incident_resolution (OBR-116): com s'ha resolt una incidència de compra. Tancar-la
// MAI mou estoc; una devolució (total o parcial) es genera a part.
export const INCIDENT_RESOLUTIONS = [
  'no_action',
  'accepted',
  'total_return',
  'partial_return',
  'credit',
] as const;
export type IncidentResolution = (typeof INCIDENT_RESOLUTIONS)[number];

// ---------------------------------------------------------------------------
// Qualitat i APPCC (MVP v1): OBR-166 homologació, OBR-167 control a la recepció,
// OBR-168 autocontrols, OBR-169 no conformitats, OBR-170 formació de manipuladors.
// Han de coincidir amb els enums de les migracions 20261101… (qualitat-*).
// ---------------------------------------------------------------------------

// supplier_homologation_status (OBR-166): Pendent és el valor per defecte de tots els proveïdors.
export const SUPPLIER_HOMOLOGATION_STATUSES = [
  'pending',
  'approved',
  'not_approved',
  'suspended',
] as const;
export type SupplierHomologationStatus = (typeof SUPPLIER_HOMOLOGATION_STATUSES)[number];

// supplier_document_type (OBR-166): el registre sanitari i els certificats opcionals (IFS, BRC, ecològic…).
export const SUPPLIER_DOCUMENT_TYPES = ['sanitary_registry', 'certificate'] as const;
export type SupplierDocumentType = (typeof SUPPLIER_DOCUMENT_TYPES)[number];

// qc_check_result (OBR-167): estat de l'envasat i de l'etiquetatge a la recepció.
export const QC_CHECK_RESULTS = ['ok', 'ko'] as const;
export type QcCheckResult = (typeof QC_CHECK_RESULTS)[number];

// qc_decision (OBR-167): què es decideix d'una línia amb un KO o una temperatura fora de rang.
export const QC_DECISIONS = ['accepted', 'accepted_with_incident', 'rejected'] as const;
export type QcDecision = (typeof QC_DECISIONS)[number];

// nc_origin (OBR-169): d'on ve una no conformitat.
export const NC_ORIGINS = ['reception', 'self_check', 'production', 'manual', 'other'] as const;
export type NcOrigin = (typeof NC_ORIGINS)[number];

// nc_status i corrective_action_status (OBR-169): Oberta → En curs → Tancada.
export const NC_STATUSES = ['open', 'in_progress', 'closed'] as const;
export type NcStatus = (typeof NC_STATUSES)[number];
export const CORRECTIVE_ACTION_STATUSES = ['open', 'in_progress', 'closed'] as const;
export type CorrectiveActionStatus = (typeof CORRECTIVE_ACTION_STATUSES)[number];

// self_check_kind / frequency / field_type / result (OBR-168).
export const SELF_CHECK_KINDS = ['cleaning', 'temperature', 'pests', 'custom'] as const;
export type SelfCheckKind = (typeof SELF_CHECK_KINDS)[number];
export const SELF_CHECK_FREQUENCIES = ['daily', 'weekly', 'monthly'] as const;
export type SelfCheckFrequency = (typeof SELF_CHECK_FREQUENCIES)[number];
export const SELF_CHECK_FIELD_TYPES = ['ok_ko', 'number', 'text', 'photo'] as const;
export type SelfCheckFieldType = (typeof SELF_CHECK_FIELD_TYPES)[number];
export const SELF_CHECK_RESULTS = ['ok', 'ko'] as const;
export type SelfCheckResult = (typeof SELF_CHECK_RESULTS)[number];

// training_topic (OBR-170).
export const TRAINING_TOPICS = ['food_handler', 'allergens', 'other'] as const;
export type TrainingTopic = (typeof TRAINING_TOPICS)[number];

// 49. sales_order_status (comanda de venda lifecycle — Vendes i Expedició).
// Partial fulfillment ("servida parcialment") is a DERIVED state (Σ servit <
// Σ demanat), never a stored member (decision Q3 = backorder): after a partial
// shipment the comanda returns to 'confirmed' until complete or closed
// manually. O2 adds 'in_production' (MTO) via ALTER TYPE; 'invoiced' is future.
export const SALES_ORDER_STATUSES = [
  'in_progress', // En curs (esborrany editable)
  'confirmed', // Confirmada (ferma, pendent de preparar; backorder torna aquí)
  'preparing', // En preparació (operari treballant-hi al picking)
  'shipped', // Expedida (tot servit, o tancada manualment amb short-ship)
  'cancelled',
] as const;
export type SalesOrderStatus = (typeof SALES_ORDER_STATUSES)[number];

// 50. sales_order_type — MTS|MTO carried from day 1 (fixed design decision) so
// the O2 MTO branch (comanda → OF) needs no model change. Always 'mts' in O1.
export const SALES_ORDER_TYPES = ['mts', 'mto'] as const;
export type SalesOrderType = (typeof SALES_ORDER_TYPES)[number];

// 51. sales_order_origin — where the comanda was born. 'integration' is the
// reserved B2B-portal hook (fora d'abast O1).
export const SALES_ORDER_ORIGINS = ['backoffice', 'planta', 'integration'] as const;
export type SalesOrderOrigin = (typeof SALES_ORDER_ORIGINS)[number];

// 52. shipment_status — expedition document (albarà de venda). Created
// 'confirmed' in one atomic picking gesture (no draft in O1); cancellation
// flips the status AND writes 'reversal' counter-movements with a reason.
export const SHIPMENT_STATUSES = ['confirmed', 'cancelled'] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

// 53. recipe_type — què ha de passar quan una OF executa aquesta recepta.
// Una d'envasat NO és una entitat a part: consumeix el granel + l'envàs i
// produeix l'article envasat, com qualsevol altra. L'únic que canvia és que la
// pantalla d'execució hi mostra el selector d'etiquetes en lloc del flux normal.
export const RECIPE_TYPES = ['normal', 'packaging'] as const;
export type RecipeType = (typeof RECIPE_TYPES)[number];

// ---------------------------------------------------------------------------
// Audit log (F0.9) — NOT Postgres enums. `audit_log.action` and `entity_type`
// are text columns (so a new module is audited with no schema change); these
// tuples are the UI-facing contract — label lookup + zod filter validation —
// shared by the backoffice viewer and the platform-console viewer.
// ---------------------------------------------------------------------------

/** Actions recorded by the automatic audit trigger + the support-view path. */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'delete', // baixa lògica (o, rarament, delete físic)
  'restore', // reactivació (deleted_at NOT NULL -> NULL)
  'support_view', // consulta del log per suport Obrava (visible pel client)
  'tenant_switch', // un compte multi-obrador comença a treballar en aquest obrador
  'packaging_labels_lost', // el tancament d'una ordre d'envasat no va poder desar la tria d'envasos
  'reception_exceptions_lost', // una recepció va entrar però no se'n van poder obrir les excepcions (OBR-83)
  'reception_quality_nc_lost', // una recepció va entrar però no se'n van poder obrir les no conformitats de qualitat (OBR-167)
  'reception_on_unavailable_order', // una recepció va arribar per a una comanda que no esperava gènere: pendent d'enviar, en curs o cancel·lada (OBR-190)
  'pin_issued', // s'ha emès el PIN d'un operari (OBR-25); el PIN mai hi consta
  'pin_regenerated', // PIN nou en lloc de l'anterior, amb motiu
  'pin_revoked', // PIN retirat (baixa de l'operari), amb motiu
  'pin_reactivated', // operari donat de baixa que torna amb un PIN nou
  'pin_converted', // una persona amb correu passa a entrar amb PIN
  'pin_login', // entrada a planta amb PIN: dispositiu i IP, mai el PIN
  'device_enrolled', // un dispositiu s'enrola per acceptar PINs
  'device_revoked', // un dispositiu deixa de poder entrar amb PIN
  'device_unlock', // un responsable desbloqueja un dispositiu bloquejat per intents
  'access_code_sent', // un administrador envia un codi d'accés nou (correu de restabliment) a una persona d'Usuaris
  'stock_without_expiry', // una recepció o un tancament d'OT ha deixat un lot sense caducitat que l'article demana (OBR-143)
  'consumption_on_unavailable_lot', // un consum o expedició ha tocat un lot bloquejat o caducat (OBR-14)
  'stock_state_change', // un lot s'ha bloquejat o desbloquejat en un lloc (OBR-14)
  'reception_missing_data', // una recepció sense comanda ha arribat sense proveïdor o sense el lot del proveïdor (OBR-87)
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/**
 * Audited table names the trigger records in `entity_type`. Kept in sync with the
 * `audited_tables` array in 20260712140000_audit-auto-triggers.sql; drives the i18n
 * entity labels and the optional entity-type filter. `audit_log` is included for the
 * support_view entry the tenant-audit-log edge function writes back to the tenant.
 */
export const AUDITED_ENTITY_TYPES = [
  'article_categories',
  'articles',
  'article_food_safety_notes',
  'article_ingredients',
  'clients',
  'client_shipping_addresses',
  'compliance_items',
  'conservation_conditions',
  'container_types',
  'devices',
  'ingredients',
  'label_templates',
  'legislation_notes',
  'packaging_units',
  'preparation_modes',
  'processes',
  'process_resources',
  'recipes',
  'recipe_versions',
  'resources',
  'resource_types',
  'sections',
  'shifts',
  'storage_locations',
  'supplier_articles',
  'suppliers',
  'taxes',
  'tech_sheets',
  'units',
  'usage_types',
  'warehouse_zones',
  'work_points',
  'users',
  'user_roles',
  'user_menu_access', // opcions de menú amagades a una persona (OBR-25)
  'supplier_documents', // documents d'homologació d'un proveïdor (OBR-166)
  'non_conformities', // registre de no conformitats (OBR-169)
  'corrective_actions', // accions correctores d'una no conformitat (OBR-169)
  'self_check_templates', // plantilles d'autocontrol (OBR-168)
  'self_check_template_fields', // camps d'una plantilla d'autocontrol (OBR-168)
  'worker_trainings', // formació dels manipuladors (OBR-170)
  'tenant_settings',
  'purchase_orders',
  'purchase_order_lines',
  'purchase_order_templates',
  'purchase_order_template_lines',
  'receptions',
  'reception_exceptions',
  'reception_corrections', // rectificacions d'albarans de recepció ja finalitzats (OBR-86)
  'purchase_returns', // devolucions de compra (OBR-117)
  'stock_state_changes', // canvis manuals d'estat d'un lot en un lloc (OBR-14)
  'stock_counts', // recomptes d'existències: crear-los, validar-los i cancel·lar-los (OBR-147)
  'lot_label_reprints', // reimpressió d'etiquetes de lot (OBR-178)
  'work_orders',
  'work_order_packaging_labels',
  'work_order_resources',
  'sales_orders',
  'sales_order_lines',
  'shipments',
  'shipment_lines',
  'audit_log',
] as const;
export type AuditedEntityType = (typeof AUDITED_ENTITY_TYPES)[number];
